import {
  authBackend,
  AuthResponse,
  VerificationResponse,
} from '../api/authBackend'

export interface User {
  id: string
  username: string
  email: string
  name: string
  avatar?: string
  verified: {
    phone: boolean
    id: boolean
  }
}

// Re-export types
export type { AuthResponse, VerificationResponse }

export function validatePassword(password: string): {
  isValid: boolean
  errors: string[]
  strength: 'weak' | 'medium' | 'strong'
} {
  const errors: string[] = []
  let strength: 'weak' | 'medium' | 'strong' = 'weak'

  if (password.length < 8) {
    errors.push('Password must be at least 8 characters')
  }

  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter')
  }

  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter')
  }

  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number')
  }

  // Calculate strength
  if (errors.length === 0) {
    if (password.length >= 12 && /[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      strength = 'strong'
    } else {
      strength = 'medium'
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    strength,
  }
}

export async function login(
  username: string,
  password: string,
): Promise<AuthResponse> {
  const result = await authBackend.authenticateUser(username, password)

  if (result.success && result.user && result.token) {
    localStorage.setItem('allhere_user', JSON.stringify(result.user))
    localStorage.setItem('allhere_token', result.token)
    localStorage.setItem('allhere_authenticated', 'true')
  }

  return result
}

export async function signup(
  username: string,
  email: string,
  password: string,
  name: string,
): Promise<AuthResponse> {
  const result = await authBackend.registerUser(username, email, password, name)

  if (result.success && result.user) {
    // Note: We don't set authenticated=true yet if verification is required
    localStorage.setItem('allhere_user', JSON.stringify(result.user))

    if (!result.verificationRequired) {
      localStorage.setItem('allhere_authenticated', 'true')
    }
  }

  return result
}

export async function requestPasswordReset(email: string) {
  return authBackend.requestPasswordReset(email)
}

export async function verifyResetCode(email: string, code: string) {
  return authBackend.verifyResetCode(email, code)
}

export async function resetPassword(resetToken: string, newPassword: string) {
  return authBackend.resetPassword(resetToken, newPassword)
}

export async function sendVerificationEmail(email: string) {
  return authBackend.sendVerificationEmail(email)
}

export async function verifyEmail(email: string, code: string) {
  const result = await authBackend.verifyEmail(email, code)
  if (result.success) {
    localStorage.setItem('allhere_authenticated', 'true')
  }
  return result
}

export function logout(): void {
  localStorage.removeItem('allhere_user')
  localStorage.removeItem('allhere_token')
  localStorage.removeItem('allhere_authenticated')
}

export function getCurrentUser(): User | null {
  const userStr = localStorage.getItem('allhere_user')
  if (!userStr) return null

  try {
    return JSON.parse(userStr)
  } catch {
    return null
  }
}

export function isAuthenticated(): boolean {
  return localStorage.getItem('allhere_authenticated') === 'true'
}

export function getSessionToken(): string | null {
  return localStorage.getItem('allhere_token')
}

export async function updateUserProfile(updates: {
  name?: string
  avatar?: string
  bio?: string
  verified?: { phone: boolean; id: boolean }
}): Promise<{ success: boolean; user?: User; error?: string }> {
  const currentUser = getCurrentUser()
  if (!currentUser) {
    return { success: false, error: 'Not authenticated' }
  }

  const result = await authBackend.updateUserProfile(currentUser.id, updates)

  if (result.success && result.user) {
    // Update localStorage with the new user data
    // Also store bio separately since it's not part of the core User type
    const userWithBio = {
      ...result.user,
      bio: updates.bio,
    }
    localStorage.setItem('allhere_user', JSON.stringify(userWithBio))
  }

  return result
}

export function getUserBio(): string {
  const userStr = localStorage.getItem('allhere_user')
  if (!userStr) return ''

  try {
    const user = JSON.parse(userStr)
    return user.bio || ''
  } catch {
    return ''
  }
}
